import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    Calendar,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    DollarSign,
    Receipt,
    Download,
    Loader,
    User,
    Briefcase,
} from "lucide-react";
import {
    getAdminNoteDeFraisInboxRequest,
    getAdminNoteDeFraisHistoryRequest,
    adminNoteDeFraisDecisionRequest,
    clearNoteDeFraisRequestMessage,
} from "../../../../redux/actions/noteDeFraisRequestActions";
import "../TraitementStyles/TraitementNotesFrais.css";
import { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../../../../../api";

const TraitementNotesFrais = () => {
    const dispatch = useDispatch();

    // Redux state
    const { adminInbox, adminHistory, loading } = useSelector((state) => state.noteDeFraisRequests);
    const user = useSelector((state) => state.auth.user);

    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    // Filtres
    const [filters, setFilters] = useState({
        typeDepense: "",
        departement: "",
        centreCout: "",
        montantMin: "",
        montantMax: "",
        dateDebut: "",
        dateFin: "",
        urgentOnly: false,
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(getAdminNoteDeFraisInboxRequest());
        dispatch(getAdminNoteDeFraisHistoryRequest());
    }, [dispatch]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        return date.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant, devise = "MAD") => {
        return new Intl.NumberFormat("fr-MA", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(montant || 0) + " dhs";
    };

    const getDownloadUrl = (path) =>
        `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path || "")}`;

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "En attente du manager", bg: "#fff3cd", color: "#856404" },
            PENDING_ADMIN: { label: "En attente de l'admin RH", bg: "#fff3cd", color: "#856404" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
            PAID: { label: "Remboursé", bg: "#d4edda", color: "#155724" },
        };
        const { label, bg, color } = config[statut] || config.PENDING_ADMIN;
        return (
            <span className="tnfBadge" style={{ backgroundColor: bg, color }}>
                {label}
            </span>
        );
    };

    const handleApprove = (noteId) => {
        // Plus de confirmation window.confirm
        dispatch(adminNoteDeFraisDecisionRequest({
            id: noteId,
            decision: true,
            comment: "",
        }));

        // Optionnel : petite indication visuelle (toast, feedback immédiat)
        // ex: toast.success("Note approuvée") si tu as react-hot-toast ou similaire

        if (showDetailsModal) {
            closeDetails();
        }

        // Rechargement (de préférence via .then() si possible)
        setTimeout(() => {
            dispatch(getAdminNoteDeFraisInboxRequest());
            dispatch(getAdminNoteDeFraisHistoryRequest());
        }, 800); // ou mieux : utiliser .unwrap() / .then() si redux-thunk
    };

    const handleReject = (note) => {
        setSelectedNote(note);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedNote) {
            alert("Veuillez saisir un motif de rejet");
            return;
        }

        dispatch(adminNoteDeFraisDecisionRequest({
            id: selectedNote.id,
            decision: false,
            comment: motifRejet,
        }));

        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedNote(null);

        // Fermer le modal de détails si ouvert
        if (showDetailsModal) {
            closeDetails();
        }

        // Recharger les données après 1 seconde
        setTimeout(() => {
            dispatch(getAdminNoteDeFraisInboxRequest());
            dispatch(getAdminNoteDeFraisHistoryRequest());
        }, 1000);
    };

    const showDetails = (note) => {
        setSelectedNote(note);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedNote(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedNote(null);
    };

    // Combiner les données selon l'onglet actif
    const allNotes = useMemo(() => {
        if (activeTab === "enCours") {
            // On ne garde QUE les statuts en attente
            return (adminInbox || []).filter(note =>
                note.status === "PENDING_ADMIN" ||
                note.status === "PENDING_MANAGER"   // selon tes vrais statuts
            );
        }

        // Pour les autres onglets, on utilise history
        return (adminHistory || []).filter(n => {
            if (activeTab === "approuve") return n.status === "APPROVED" || n.status === "PAID";
            if (activeTab === "rejete") return n.status === "REJECTED";
            return true;
        });
    }, [activeTab, adminInbox, adminHistory]);

    const getFilteredNotes = () => {
        let filtered = allNotes;

        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            filtered = filtered.filter((n) =>
                n.employeeName?.toLowerCase().includes(s) ||
                n.employeeMatricule?.toLowerCase().includes(s) ||
                n.category?.toLowerCase().includes(s) ||
                n.requestType?.toLowerCase().includes(s) ||
                n.description?.toLowerCase().includes(s) ||
                n.centreCouts?.toLowerCase().includes(s)
            );
        }

        if (filters.typeDepense) {
            filtered = filtered.filter((n) => (n.category || n.requestType) === filters.typeDepense);
        }
        if (filters.departement) {
            filtered = filtered.filter((n) => n.employeeMatricule === filters.departement);
        }
        if (filters.centreCout) {
            filtered = filtered.filter((n) => n.centreCouts?.includes(filters.centreCout));
        }
        if (filters.montantMin) {
            filtered = filtered.filter((n) => n.montantDepense >= parseFloat(filters.montantMin));
        }
        if (filters.montantMax) {
            filtered = filtered.filter((n) => n.montantDepense <= parseFloat(filters.montantMax));
        }
        if (filters.dateDebut) {
            filtered = filtered.filter((n) => n.dateDepense >= filters.dateDebut);
        }
        if (filters.dateFin) {
            filtered = filtered.filter((n) => n.dateDepense <= filters.dateFin);
        }
        if (filters.urgentOnly) {
            filtered = filtered.filter((n) => n.urgent);
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        // Compteur "En cours" = nombre de notes réellement en attente (même filtre que allNotes)
        const enCoursCount = (adminInbox || []).filter(note =>
            note.status === "PENDING_ADMIN" ||
            note.status === "PENDING_MANAGER"
        ).length;

        const approuveCount = (adminHistory || []).filter(n =>
            n.status === "APPROVED" || n.status === "PAID"
        ).length;

        const rejeteCount = (adminHistory || []).filter(n =>
            n.status === "REJECTED"
        ).length;

        return [
            { id: "enCours", label: "En cours", count: enCoursCount },
            { id: "approuve", label: "Approuvées", count: approuveCount },
            { id: "rejete", label: "Rejetées", count: rejeteCount },
        ];
    }, [adminInbox, adminHistory]);

    const typesDepense = useMemo(() => {
        const types = new Set();
        allNotes.forEach(n => {
            if (n.category || n.requestType) types.add(n.category || n.requestType);
        });
        return Array.from(types);
    }, [allNotes]);

    const departements = useMemo(() => {
        const depts = new Set();
        allNotes.forEach(n => {
            if (n.employeeMatricule) depts.add(n.employeeMatricule);
        });
        return Array.from(depts);
    }, [allNotes]);

    const totalMontant = useMemo(
        () => getFilteredNotes().reduce((sum, n) => sum + (n.montantDepense || 0), 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allNotes, searchTerm, filters]
    );

    const depassementsCount = useMemo(() => {
        return adminInbox?.filter(n => {
            const plafond = n.noteDeFrais?.montantMinimum;
            return plafond && n.montantDepense > plafond;
        }).length || 0;
    }, [adminInbox]);

    const urgentCount = useMemo(() => {
        return adminInbox?.filter(n => n.urgent).length || 0;
    }, [adminInbox]);

    const filteredNotes = getFilteredNotes();

    const getCouleurByCategorie = (categorie) => {
        const colors = {
            TRANSPORT: "#3b82f6",
            HEBERGEMENT: "#8b5cf6",
            RESTAURATION: "#10b981",
            AUTRE: "#f59e0b",
        };
        return colors[categorie] || "#6b7280";
    };

    if (loading && allNotes.length === 0) {
        return (
            <div className="tnfPage">
                <div className="tnfWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tnfPage">
            <div className="tnfWrap">
                {/* Header */}
                <div className="tnfHeader">
                    <div>
                        <h1>Traitement des notes de frais</h1>
                        <p>Gérez et validez les notes de frais en attente de validation RH</p>
                    </div>
                </div>

                {/* Stats */}
                {/*<div className="tnfStats">*/}
                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">En attente</div>*/}
                {/*            <div className="tnfStatValue">*/}
                {/*                {(adminInbox || []).filter(note =>*/}
                {/*                    note.status === "PENDING_ADMIN" ||*/}
                {/*                    note.status === "PENDING_MANAGER"*/}
                {/*                ).length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <Clock size={28} className="tnfStatIcon tnfStatIcon--warn" />*/}
                {/*    </div>*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Montant total</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--money">{formatMontant(totalMontant)}</div>*/}
                {/*        </div>*/}
                {/*        <DollarSign size={28} className="tnfStatIcon tnfStatIcon--accent" />*/}
                {/*    </div>*/}

                {/*    /!*<div className="tnfStatCard">*!/*/}
                {/*    /!*    <div>*!/*/}
                {/*    /!*        <div className="tnfStatLabel">Urgentes</div>*!/*/}
                {/*    /!*        <div className="tnfStatValue tnfStatValue--orange">{urgentCount}</div>*!/*/}
                {/*    /!*    </div>*!/*/}
                {/*    /!*    <AlertCircle size={28} className="tnfStatIcon tnfStatIcon--orange" />*!/*/}
                {/*    /!*</div>*!/*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Approuvées</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--ok">*/}
                {/*                {adminHistory?.filter(n => n.status === "APPROVED" || n.status === "PAID").length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <Check size={28} className="tnfStatIcon tnfStatIcon--ok" />*/}
                {/*    </div>*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Rejetées</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--no">*/}
                {/*                {adminHistory?.filter(n => n.status === "REJECTED").length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <X size={28} className="tnfStatIcon tnfStatIcon--no" />*/}
                {/*    </div>*/}
                {/*</div>*/}

                {/* Search + Filters */}
                <div className="tnfCard">
                    <div className="tnfControls">
                        <div className="tnfSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par nom, matricule, catégorie, description..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button className="tnfFilterBtn" type="button" onClick={() => setShowFilters((v) => !v)}>
                            <Filter size={16} />
                            Filtres
                            <ChevronDown size={16} className={`tnfChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tnfFilters">
                            <div className="tnfField">
                                <label>Type de dépense</label>
                                <select value={filters.typeDepense} onChange={(e) => setFilters((p) => ({ ...p, typeDepense: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {typesDepense.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="tnfField">
                                <label>Département</label>
                                <select value={filters.departement} onChange={(e) => setFilters((p) => ({ ...p, departement: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {departements.map((d) => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </div>

                            {/*<div className="tnfField">*/}
                            {/*    <label>Centre de coût</label>*/}
                            {/*    <input*/}
                            {/*        type="text"*/}
                            {/*        value={filters.centreCout}*/}
                            {/*        onChange={(e) => setFilters((p) => ({ ...p, centreCout: e.target.value }))}*/}
                            {/*        placeholder="Filtrer par centre"*/}
                            {/*    />*/}
                            {/*</div>*/}

                            <div className="tnfField">
                                <label>Montant min</label>
                                <input type="number" value={filters.montantMin} onChange={(e) => setFilters((p) => ({ ...p, montantMin: e.target.value }))} placeholder="0" />
                            </div>

                            <div className="tnfField">
                                <label>Montant max</label>
                                <input type="number" value={filters.montantMax} onChange={(e) => setFilters((p) => ({ ...p, montantMax: e.target.value }))} placeholder="∞" />
                            </div>

                            <div className="tnfField">
                                <label>Date début</label>
                                <input type="date" value={filters.dateDebut} onChange={(e) => setFilters((p) => ({ ...p, dateDebut: e.target.value }))} />
                            </div>

                            <div className="tnfField">
                                <label>Date de fin</label>
                                <input type="date" value={filters.dateFin} onChange={(e) => setFilters((p) => ({ ...p, dateFin: e.target.value }))} />
                            </div>

                            <div className="tnfField">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={filters.urgentOnly}
                                        onChange={(e) => setFilters((p) => ({ ...p, urgentOnly: e.target.checked }))}
                                    />
                                    <span style={{ marginLeft: 8 }}>Urgentes uniquement</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs + Table */}
                <div className="tnfCard">
                    <div className="tnfTabs">
                        {tabs.map((tab) => (
                            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`tnfTab ${activeTab === tab.id ? "is-active" : ""}`}>
                                {tab.label}
                                <span className={`tnfCount ${activeTab === tab.id ? "is-active" : ""}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="tnfTableWrap">
                        {filteredNotes.length === 0 ? (
                            <div className="tnfEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune note de frais trouvée</div>
                            </div>
                        ) : (
                            <table className="tnfTable">
                                <thead>
                                    <tr>
                                        <th>Collaborateur</th>
                                        <th>Catégorie</th>
                                        <th>Description</th>
                                        {/*<th>Centre de coût</th>*/}
                                        <th>Date dépense</th>
                                        <th>Montant</th>
                                        <th>Justif.</th>
                                        {/*<th>Statut</th>*/}
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredNotes.map((note) => (
                                        <tr
                                            key={note.id}
                                            className={`${note.urgent ? "tnfRowUrgent" : ""}`}
                                            title={note.urgent ? "Note urgente" : ""}
                                        >
                                            <td>
                                                <div className="tnfName">
                                                    <span className="tnfPrimary">
                                                        {note.employeeName || "N/A"}
                                                    </span>
                                                    {note.urgent && <span className="tnfFire">🔥</span>}
                                                </div>
                                                <div className="tnfSub">
                                                    {note.employeeMatricule || "-"}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="tnfType">
                                                    <span
                                                        className="tnfDot"
                                                        style={{ backgroundColor: getCouleurByCategorie(note.category || note.requestType) }}
                                                    />
                                                    <span className="tnfPrimary">
                                                        {note.category || note.requestType || "-"}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="tnfDesc">
                                                <div className="tnfTrunc" title={note.description}>
                                                    {note.description || "-"}
                                                </div>
                                            </td>

                                            <td><span className="tnfMuted">{formatDate(note.dateDepense)}</span></td>

                                            <td>
                                                <div className="tnfAmount">
                                                    <div className="tnfPrimary tnfAmountMain">{formatMontant(note.montantDepense)}</div>
                                                    {note.montantValide && note.montantValide !== note.montantDepense && (
                                                        <div className="tnfSub">Validé: {formatMontant(note.montantValide)}</div>
                                                    )}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="tnfJustif">
                                                    <span>{note.justificatifs?.length || 0}</span>
                                                </div>
                                            </td>

                                            {/*<td>{getStatutBadge(note.status)}</td>*/}

                                            <td className="is-right">
                                                <div className="tnfRowActions">
                                                    <button type="button" className="tnfIconBtn" onClick={() => showDetails(note)} title="Voir détails">
                                                        <FileText size={16} />
                                                    </button>

                                                    {activeTab === "enCours" && (
                                                        <>
                                                            <button type="button" className="tnfActionBtn tnfActionBtn--ok" onClick={() => handleApprove(note.id)} title="Approuver">
                                                                <Check size={16} />
                                                            </button>
                                                            <button type="button" className="tnfActionBtn tnfActionBtn--no" onClick={() => handleReject(note)} title="Refuser">
                                                                <X size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL */}
            {showDetailsModal && selectedNote && (
                <div className="tnfModal">
                    <div className="tnfModal__panel" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon">
                                    <FileText size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">Détails de la note de frais</div>
                                    <div className="tnfModal__sub">
                                        {selectedNote.employeeName || "N/A"}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeDetails} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            {/* Alerte urgence */}
                            {selectedNote.urgent && (
                                <div className="tnfAlert tnfAlert--orange">
                                    <AlertCircle size={18} />
                                    <div>
                                        <div className="tnfAlert__title">Note de frais urgente</div>
                                        <div className="tnfAlert__text">Cette demande nécessite un traitement prioritaire</div>
                                    </div>
                                </div>
                            )}

                            {/* Informations collaborateur */}
                            <div className="tnfPanel">
                                <div className="tnfPanel__title">Informations collaborateur</div>
                                <div className="tnfGrid2">
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">Nom complet</div>
                                        <div className="tnfInfo__value">
                                            {selectedNote.employeeName || "-"}
                                        </div>
                                    </div>
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">Matricule</div>
                                        <div className="tnfInfo__value">{selectedNote.employeeMatricule || "-"}</div>
                                    </div>
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">ID Employé</div>
                                        <div className="tnfInfo__value">{selectedNote.employeeId || "-"}</div>
                                    </div>
                                    {/*<div className="tnfInfo">*/}
                                    {/*    <div className="tnfInfo__label">Centre de coût</div>*/}
                                    {/*    <div className="tnfInfo__value">{selectedNote.centreCouts || "-"}</div>*/}
                                    {/*</div>*/}
                                </div>
                            </div>

                            {/* Détails dépense */}
                            <div className="tnfBlock">
                                <div className="tnfBlock__title">Détails de la dépense</div>

                                <div className="tnfLine">
                                    <Receipt size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Catégorie</div>
                                        <div className="tnfLine__value">
                                            {selectedNote.category || selectedNote.requestType || "-"}
                                        </div>
                                    </div>
                                </div>

                                <div className="tnfLine">
                                    <Calendar size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Date dépense / soumission</div>
                                        <div className="tnfLine__value">
                                            {formatDate(selectedNote.dateDepense)} — soumise le {formatDate(selectedNote.createdAt)}
                                        </div>
                                    </div>
                                </div>

                                <div className="tnfLine">
                                    <DollarSign size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Montant demandé</div>
                                        <div className="tnfLine__value">{formatMontant(selectedNote.montantDepense)}</div>
                                    </div>
                                </div>

                                {selectedNote.montantValide && (
                                    <div className="tnfLine">
                                        <DollarSign size={16} />
                                        <div className="tnfLine__content">
                                            <div className="tnfLine__label">Montant validé</div>
                                            <div className="tnfLine__value">{formatMontant(selectedNote.montantValide)}</div>
                                        </div>
                                    </div>
                                )}

                                {/*<div className="tnfLine">*/}
                                {/*    <Briefcase size={16} />*/}
                                {/*    <div className="tnfLine__content">*/}
                                {/*        <div className="tnfLine__label">Centre de coût</div>*/}
                                {/*        <div className="tnfLine__value">{selectedNote.centreCouts || "-"}</div>*/}
                                {/*    </div>*/}
                                {/*</div>*/}

                                {selectedNote.ordreMissionLibelle && (
                                    <div className="tnfLine">
                                        <FileText size={16} />
                                        <div className="tnfLine__content">
                                            <div className="tnfLine__label">Ordre de mission</div>
                                            <div className="tnfLine__value">
                                                {selectedNote.ordreMissionCode} - {selectedNote.ordreMissionLibelle}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="tnfLine">
                                    <FileText size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Description</div>
                                        <div className="tnfLine__value">{selectedNote.description || "-"}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Validation manager */}
                            {selectedNote.managerDecision !== null && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Validation manager</div>
                                    <div className="tnfPanel">
                                        <div className="tnfGrid2">
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Manager</div>
                                                <div className="tnfInfo__value">
                                                    {selectedNote.managerName || "-"}
                                                </div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Décision</div>
                                                <div className="tnfInfo__value">
                                                    {selectedNote.managerDecision ? (
                                                        <span style={{ color: "#155724" }}>✓ Approuvé</span>
                                                    ) : (
                                                        <span style={{ color: "#721c24" }}>✗ Rejeté</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Date</div>
                                                <div className="tnfInfo__value">{formatDate(selectedNote.managerDecisionAt)}</div>
                                            </div>
                                        </div>
                                        {selectedNote.managerComment && (
                                            <div style={{ marginTop: 12, padding: 12, background: "#f9fafb", borderRadius: 6 }}>
                                                <div className="tnfInfo__label">Commentaire</div>
                                                <div style={{ marginTop: 4 }}>{selectedNote.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Justificatifs */}
                            {selectedNote.justificatifs && selectedNote.justificatifs.length > 0 && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Justificatifs ({selectedNote.justificatifs.length})</div>
                                    <div className="tnfDocs">
                                        {selectedNote.justificatifs.map((j, idx) => (
                                            <a key={idx} className="tnfDoc" href={getDownloadUrl(j)} target="_blank" rel="noreferrer">
                                                <FileText size={18} />
                                                <div className="tnfDoc__meta">
                                                    <div className="tnfDoc__name">{j}</div>
                                                </div>
                                                <Download size={16} className="tnfDoc__dl" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Informations de validation (historique) */}
                            {(selectedNote.status === "APPROVED" || selectedNote.status === "REJECTED" || selectedNote.status === "PAID") && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Informations de validation RH</div>
                                    <div className="tnfPanel">
                                        <div className="tnfGrid2">
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Statut</div>
                                                <div className="tnfInfo__value">{getStatutBadge(selectedNote.status)}</div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Date de validation</div>
                                                <div className="tnfInfo__value">{formatDate(selectedNote.adminDecisionAt)}</div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Validé par</div>
                                                <div className="tnfInfo__value">
                                                    {selectedNote.adminName || "-"}
                                                </div>
                                            </div>
                                            {selectedNote.status === "APPROVED" && (
                                                <div className="tnfInfo">
                                                    <div className="tnfInfo__label">Montant validé</div>
                                                    <div className="tnfInfo__value tnfValueOk">
                                                        {formatMontant(selectedNote.montantValide || selectedNote.montantDepense)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {selectedNote.adminComment && (
                                            <div className="tnfRejectReason">
                                                <div className="tnfRejectReason__label">Commentaire RH</div>
                                                <div className="tnfRejectReason__text">{selectedNote.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="tnfModal__footer">
                            {activeTab === "enCours" && (
                                <>
                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ghostDanger"
                                        onClick={() => {
                                            closeDetails();
                                            handleReject(selectedNote);
                                        }}
                                    >
                                        Rejeter
                                    </button>

                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ok"
                                        onClick={() => {
                                            handleApprove(selectedNote.id);
                                            closeDetails();
                                        }}
                                    >
                                        Approuver
                                    </button>
                                </>
                            )}

                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDetails}>
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* REJECT MODAL */}
            {showRejectModal && selectedNote && (
                <div className="tnfModal">
                    <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon tnfModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">Refuser la note de frais</div>
                                    <div className="tnfModal__sub">
                                        {selectedNote.employeeName || "N/A"}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeReject} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            <div className="tnfMiniCard">
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Catégorie :</span>
                                    <span className="tnfStrong">
                                        {selectedNote.category || selectedNote.requestType || "-"}
                                    </span>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Montant :</span>
                                    <span className="tnfStrong tnfStrong--accent">
                                        {formatMontant(selectedNote.montantDepense)}
                                    </span>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Description :</span>
                                    <span className="tnfStrong">{selectedNote.description || "-"}</span>
                                </div>

                                {selectedNote.urgent && (
                                    <div className="tnfMiniWarn">
                                        <AlertCircle size={16} />
                                        <span>Note de frais urgente</span>
                                    </div>
                                )}
                            </div>

                            <label className="tnfLabel">
                                Motif du rejet <span className="tnfReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Veuillez préciser le motif du rejet de cette note de frais..."
                                className="tnfTextarea"
                            />
                            <div className="tnfHelp">Ce motif sera communiqué au collaborateur</div>
                        </div>

                        <div className="tnfModal__footer">
                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeReject}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tnfBtn tnfBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? "En cours..." : "Confirmer le rejet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementNotesFrais;
